import type {
  DicomSyntaxConfig,
  AbstractSyntaxConfig,
  TransferSyntaxValue,
} from '../data/syntax-config';

// Map SOP Class UIDs to frontend syntax config keys
const sopClassUidToConfigKey: Record<string, keyof DicomSyntaxConfig> = {
  '1.2.840.10008.5.1.4.1.1.7': 'secondaryCaptureImageStorage',
  '1.2.840.10008.5.1.4.1.1.6.1': 'ultrasoundImageStorage',
  '1.2.840.10008.5.1.4.1.1.3.1': 'ultrasoundMultiFrameImageStorage',
  '1.2.840.10008.1.1': 'verificationSopClass',
};

// Map Transfer Syntax UIDs to frontend config keys
const transferSyntaxUidToConfigKey: Record<string, keyof AbstractSyntaxConfig> =
  {
    '1.2.840.10008.1.2.2': 'explicitVrBigEndian',
    '1.2.840.10008.1.2.1': 'explicitVrLittleEndian',
    '1.2.840.10008.1.2': 'implicitVrLittleEndian',
    '1.2.840.10008.1.2.4.91': 'jpeg2000ImageCompression',
    '1.2.840.10008.1.2.4.90': 'jpeg2000ImageCompressionLosslessOnly',
    '1.2.840.10008.1.2.4.50': 'jpegBaselineProcess1',
    '1.2.840.10008.1.2.4.70':
      'jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1',
    '1.2.840.10008.1.2.4.80': 'jpegLsLosslessImageCompression',
    '1.2.840.10008.1.2.4.81': 'jpegLsLossyNearLosslessImageCompression',
  };

interface BackendAbstractSyntax {
  sopClassUid: string;
  commonName: string;
  enabled: boolean;
  transferSyntaxes: string[];
}

/**
 * Transform backend abstract syntax data to frontend DicomSyntaxConfig format
 */
export function transformBackendSyntaxConfig(
  abstractSyntaxes: BackendAbstractSyntax[]
): DicomSyntaxConfig {
  // Initialize with all transfer syntaxes as rejected
  const initializeAbstractSyntax = (): AbstractSyntaxConfig => ({
    explicitVrBigEndian: 'rejected',
    explicitVrLittleEndian: 'rejected',
    implicitVrLittleEndian: 'rejected',
    jpeg2000ImageCompression: 'rejected',
    jpeg2000ImageCompressionLosslessOnly: 'rejected',
    jpegBaselineProcess1: 'rejected',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      'rejected',
    jpegLsLosslessImageCompression: 'rejected',
    jpegLsLossyNearLosslessImageCompression: 'rejected',
  });

  const syntaxConfig: DicomSyntaxConfig = {
    secondaryCaptureImageStorage: initializeAbstractSyntax(),
    ultrasoundImageStorage: initializeAbstractSyntax(),
    ultrasoundMultiFrameImageStorage: initializeAbstractSyntax(),
    verificationSopClass: {
      explicitVrBigEndian: 'rejected',
      explicitVrLittleEndian: 'rejected',
      implicitVrLittleEndian: 'rejected',
    },
  };

  // Process each abstract syntax from backend
  abstractSyntaxes.forEach((abstractSyntax) => {
    const configKey = sopClassUidToConfigKey[abstractSyntax.sopClassUid];

    if (configKey && abstractSyntax.enabled) {
      // Set supported transfer syntaxes to 'accepted'
      abstractSyntax.transferSyntaxes.forEach((transferSyntaxUid) => {
        const transferSyntaxKey =
          transferSyntaxUidToConfigKey[transferSyntaxUid];

        if (transferSyntaxKey) {
          if (configKey === 'verificationSopClass') {
            // Verification SOP Class has partial config
            const verificationConfig =
              syntaxConfig.verificationSopClass as Partial<AbstractSyntaxConfig>;
            if (transferSyntaxKey in verificationConfig) {
              (verificationConfig as any)[transferSyntaxKey] = 'accepted';
            }
          } else {
            // Other abstract syntaxes have full config
            (syntaxConfig[configKey] as AbstractSyntaxConfig)[
              transferSyntaxKey
            ] = 'accepted';
          }
        }
      });
    }
  });

  return syntaxConfig;
}
